var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { keyBy } from 'lodash';
import * as qs from 'qs';
import { AddCapabilities, SetCardPrograms } from '@Actions/cardsActions';
import { ReceiveLinkedAccounts, RequestLinkedAccounts } from '@Actions/currentUserActions';
import { store } from '@ReactStore/store';
import { Api as apiService } from '../api';
export class LinkedAccountsApiService {
    /**
     * @summary Gets all alternate emails for the currency user, either from the Redux Store or via the API
     * @param {boolean} overrideStore if true, circumvents checking the store and makes an API request
     *
     */
    static get(overrideStore = false) {
        if (!overrideStore) {
            const currentUser = store.getState().currentUser;
            if (currentUser && currentUser.cards) {
                return Promise.resolve(currentUser.cards);
            }
        }
        // request action
        store.dispatch(new RequestLinkedAccounts());
        return apiService
            .get('/api/web/accounts/cards')
            .then((response) => response.json())
            .then((linkedAccounts) => {
            // success action
            store.dispatch(new ReceiveLinkedAccounts(linkedAccounts));
            return linkedAccounts;
        });
    }
    static getPendingOrDisconnectedPlaidAccount(destinationType) {
        return apiService.get(`/api/web/accounts/plaid/${destinationType}`).then((response) => response === null || response === void 0 ? void 0 : response.json());
    }
    static getPlaidAccount() {
        return apiService.get('/api/web/accounts/company/plaid_account').then((response) => response === null || response === void 0 ? void 0 : response.json());
    }
    static getUserPlaidAccount() {
        return apiService.get('/api/web/accounts/user/plaid_account').then((response) => response === null || response === void 0 ? void 0 : response.json());
    }
    static cancel(linkedAccountId) {
        return apiService
            .patch(`/api/web/accounts/${linkedAccountId}/cancel`)
            .then((response) => response.json())
            .then((linkedAccount) => linkedAccount);
    }
    static getCompanyCardPrograms({ overrideStore, onlyPending, includeStatementCycle, includeSubaccounts = true }) {
        if (!overrideStore && !onlyPending) {
            const state = store.getState();
            const programs = state.cards && state.cards.programs;
            if (programs.length) {
                return programs;
            }
        }
        return apiService
            .get(`/api/web/accounts/subaccounts?includePending=${onlyPending}&includeStatementCycle=${includeStatementCycle}&includeSubaccounts=${includeSubaccounts}`)
            .then((response) => response.json())
            .then((cardPrograms) => {
            if (onlyPending) {
                return cardPrograms.filter((x) => x.state === 'pending');
            }
            store.dispatch(new SetCardPrograms(cardPrograms));
            return cardPrograms;
        });
    }
    static getCompanyCorporateCards() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = '/api/web/accounts/company/cards?includes[user]=&includes[parent_account]';
            return yield apiService.responseToJSONAndToastError(apiService.get(url));
        });
    }
    static getExpiringCards(filters) {
        return __awaiter(this, void 0, void 0, function* () {
            let params = new URLSearchParams({
                'filters[allowanceEndDateBefore]': filters.allowanceEndDateBefore,
                'filters[allowanceEndDateAfter]': filters.allowanceEndDateAfter
            });
            const url = `/api/web/accounts/cards/expiring?${params}`;
            return yield apiService.responseToJSONAndToastError(apiService.get(url));
        });
    }
    static updateCompanyCardPrograms(cards) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield apiService.post('/api/web/accounts/subaccounts', cards);
            return yield response.json();
        });
    }
    static getAccountCapabilities(cardProgramId) {
        return apiService
            .get(`/api/web/accounts/cardprograms/${cardProgramId}/capabilities`)
            .then((response) => response.json())
            .then((resp) => resp.capabilities);
    }
    static getGatewayProgramsCapabilities(overrideStore) {
        return apiService
            .get(`/api/web/accounts/capabilities`)
            .then((res) => res.json())
            .then((res) => res.capabilities)
            .then((capabilities) => {
            if (overrideStore) {
                store.dispatch(new AddCapabilities(keyBy(capabilities, 'programId')));
            }
            return capabilities;
        });
    }
    static getGatewaySessionUrl(cardProgramId, redirectUrl) {
        return apiService
            .get(`/api/web/accounts/cardprograms/${cardProgramId}/session?&redirect_url=${encodeURI(redirectUrl)}`)
            .then((response) => response.json())
            .then((resp) => resp.sessionUrl);
    }
    static connectGatewayCardProgram(connectTo, connectionType, redirectUrl) {
        const postBody = {
            connectTo,
            connectionType,
            redirectUrl
        };
        return apiService.post('/api/web/accounts/cardprograms?provider=gateway', postBody).then((response) => response.json());
    }
    static addCards(cardProgramId, userData) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield apiService.post(`/api/web/accounts/cardprograms/${cardProgramId}`, userData);
            return yield response.json();
        });
    }
    static addProgram(bankData) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield apiService.post('/api/web/accounts/cardprograms', bankData);
            return yield response.json();
        });
    }
    static suspendCard(linkedAccountId) {
        return apiService
            .post(`/api/accounts/${linkedAccountId}/suspend`)
            .then((response) => response.json())
            .then((linkedAccount) => linkedAccount);
    }
    static unsuspendCard(linkedAccountId) {
        return apiService
            .post(`/api/accounts/${linkedAccountId}/unsuspend`)
            .then((response) => response.json())
            .then((linkedAccount) => linkedAccount);
    }
    static deleteCard(linkedAccountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield apiService.delete(`/api/web/accounts/${linkedAccountId}`);
            return yield response.json();
        });
    }
    static skipCardTransactions(beforeDate, accountIds) {
        return __awaiter(this, void 0, void 0, function* () {
            yield apiService.post('/api/web/accounts/cards/skiptransactions', {
                before: beforeDate.valueOf(),
                account_ids: accountIds
            });
        });
    }
    static suspendProgram(linkedAccountId) {
        return apiService.responseToJSONAndToastError(apiService.patch(`/api/web/accounts/${linkedAccountId}/pause`));
    }
    static deleteProgram(linkedAccountId) {
        return apiService.responseToJSONAndToastError(apiService.delete(`/api/web/accounts/${linkedAccountId}`));
    }
    static updateCardProgramName(cardProgramId, name) {
        return __awaiter(this, void 0, void 0, function* () {
            return apiService.responseToJSON(apiService.patch(`/api/web/accounts/cardprograms/${cardProgramId}/name`, { name: name }));
        });
    }
    static updateCardProgramStatementDate(cardProgramId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return apiService.responseToJSONAndToastError(apiService.patch(`/api/web/accounts/cardprograms/${cardProgramId}/statement_date`, data));
        });
    }
    static getProgramCardCounts() {
        return apiService.responseToJSONAndToastError(apiService.get('/api/web/accounts/subaccounts/counts'));
    }
    static removeCardErrors(linkedAccountId) {
        return apiService.responseToJSONAndToastError(apiService.delete(`/api/web/accounts/${linkedAccountId}/errors`));
    }
    static searchCardsForProgram(parentLinkedAccountIds, // Card program ID
    limit, offset, sortField, sortDirection, filters, search = '', includeAllowance, includeSharedLink, includeUnexpensedTransactionCount, types) {
        const searchParams = new URLSearchParams({
            limit: limit === null || limit === void 0 ? void 0 : limit.toString(),
            offset: offset === null || offset === void 0 ? void 0 : offset.toString(),
            search: search === null || search === void 0 ? void 0 : search.toString()
        });
        if (sortField) {
            searchParams.append('sortField', sortField);
            searchParams.append('sortDirection', sortDirection);
        }
        if (filters) {
            searchParams.append('filters', JSON.stringify(apiService.parseFilters(filters)));
        }
        const include = {
            allowance: includeAllowance,
            json_image: includeSharedLink,
            unexpensed_transaction_count: includeUnexpensedTransactionCount
        };
        searchParams.append('include', JSON.stringify(include));
        if (typeof parentLinkedAccountIds === 'string') {
            searchParams.append('programIds[]', parentLinkedAccountIds);
        }
        else {
            parentLinkedAccountIds === null || parentLinkedAccountIds === void 0 ? void 0 : parentLinkedAccountIds.forEach((parentLinkedAccountId) => {
                searchParams.append('programIds[]', parentLinkedAccountId);
            });
        }
        if (types) {
            types.forEach((type) => searchParams.append('types[]', type));
        }
        return apiService.responseToJSONAndToastError(apiService.get(`api/web/accounts/cardprograms/cards?${searchParams}`));
    }
    static getCardsPaginated({ limit, offset, sortField = 'created_at', sortDirection = 'asc', includeAllowance = true, state, type }) {
        const queryParams = qs.stringify({
            limit: limit === null || limit === void 0 ? void 0 : limit.toString(),
            offset: offset === null || offset === void 0 ? void 0 : offset.toString(),
            state: state,
            sortField,
            sortDirection,
            include: includeAllowance ? 'allowance' : undefined,
            type
        });
        return apiService.responseToJSONAndToastError(apiService.get(`api/web/accounts/cards/paginate?${queryParams}`));
    }
    static getStatistics() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield apiService.get(`api/web/accounts/cards/stats`);
            return yield response.json();
        });
    }
    static getActiveCardCountStatistics() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield apiService.get(`api/web/accounts/cards/active_stats`);
            return yield response.json();
        });
    }
    static getStatisticsByCardType() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield apiService.get(`api/web/accounts/cards/stats_by_type`);
            return yield response.json();
        });
    }
    static getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield apiService.get(`api/web/accounts/cards/balance`);
            return yield response.json();
        });
    }
    static setCard(cardData, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let query = '';
            if (options === null || options === void 0 ? void 0 : options.subaccounts) {
                query += '&subaccounts=1';
            }
            const response = yield apiService.post(`/api/web/accounts/cards?${query}`, cardData);
            return yield response.json();
        });
    }
    static reconnectAccount(accountId, requestBody) {
        return apiService.patch(`/api/web/accounts/${accountId}`, requestBody).then((response) => response.json());
    }
    static getFinicityAccounts(requestBody, options) {
        const queryParams = qs.stringify({
            subaccounts: (options === null || options === void 0 ? void 0 : options.subaccounts) ? '1' : undefined
        });
        return apiService.post(`/api/web/accounts/cards/auth?${queryParams}`, requestBody).then((response) => response.json());
    }
    static makeDemoTransaction(linkedAccountId) {
        return apiService.responseToJSONAndToastError(apiService.post(`/api/web/accounts/${linkedAccountId}/makeDemoTransaction`));
    }
}
